import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import Filter from "../../elements/filter";
import { SeasonTabs } from "../../elements/seasonCalendar";
import { DaypartTabs } from "../../elements/dayparts";
import { seasonRange } from "../../elements/seasonDates";
import Loader from "../../../settings/loader";
import {
  encodeBase64Json,
  getDaypartFilter,
  getMenuReport,
} from "../menuReports/api";
import MenuDashboard from "./views/menuDashboard";
import MultiOutletComparison from "./views/multiOutletComparison";
import MenuOrder from "./views/menuOrder";
import { getOutletName } from "./menuEngineeringUtils";
import { getPreviousPeriod } from "./views/menuDashboardInsights";
import "./tokens.css";
import "./style.scss";

const PAGES = {
  "menu-dashboard": {
    title: "Menu Engineering",
    description: "See what sells, what earns, and where to focus next.",
  },
  "multi-outlet-comparison": {
    title: "Multi Outlet Comparison",
    description: "Compare menu performance across your selected outlets.",
  },
  "menu-order": {
    title: "Menu Order",
    description: "Preview a recommended item order using menu performance.",
  },
};

function MenuEngineering() {
  const location = useLocation();
  const { user, chimeraserv, setFilterValue } = useAppContext();
  const [filter, setFilter] = useState(null);
  const [baseFilter, setBaseFilter] = useState(null);
  const [seasonError, setSeasonError] = useState("");
  const [daypartError, setDaypartError] = useState("");
  const [report, setReport] = useState({
    loading: false,
    outlets: [],
    error: null,
  });
  const currentPage = location.pathname.split("/").pop();
  const page = PAGES[currentPage] || PAGES["menu-dashboard"];
  const seasonBrands = baseFilter?.data?.brands || [];
  const seasonBrandId =
    seasonBrands.length === 1 ? String(seasonBrands[0].braKey) : "";

  useEffect(() => {
    setFilter(null);
    setBaseFilter(null);
    setSeasonError("");
    setDaypartError("");
    setFilterValue(null);
    setReport({ loading: false, outlets: [], error: null });
  }, [location.pathname, setFilterValue]);

  const handleSetFilter = useCallback(
    (nextFilter) => {
      setBaseFilter(nextFilter);
      setSeasonError("");
      setDaypartError("");
      setFilter(nextFilter);
      setFilterValue(nextFilter);
    },
    [setFilterValue]
  );

  const withDaypart = (nextFilter, daypart) => {
    const query = JSON.parse(window.atob(nextFilter.base64));
    delete query.daypart;
    if (daypart) query.daypart = getDaypartFilter(daypart);
    return {
      ...nextFilter,
      data: { ...nextFilter.data, daypart: daypart || null },
      base64: encodeBase64Json(query),
      timestamp: Date.now(),
    };
  };

  const selectDaypart = (daypart) => {
    if (!filter) return;
    setDaypartError("");
    try {
      if (daypart && String(daypart.braKey) !== seasonBrandId)
        throw new Error("Select the daypart's brand.");
      const nextFilter = withDaypart(filter, daypart);
      setFilter(nextFilter);
      setFilterValue(nextFilter);
    } catch (error) {
      setDaypartError(error.message);
    }
  };

  const selectSeason = (season, timeZone) => {
    if (!baseFilter) return;
    setSeasonError("");
    try {
      if (!season) {
        const restored = withDaypart(baseFilter, filter?.data?.daypart);
        setFilter(restored);
        setFilterValue(restored);
        return;
      }
      if (String(season.brandId) !== seasonBrandId)
        throw new Error("Select the season's brand.");
      const range = seasonRange(season, timeZone);
      const nextFilter = {
        ...baseFilter,
        data: {
          ...baseFilter.data,
          date: { start: range.start.short, end: range.end.short },
          season: { ...season, businessDates: range.businessDates },
          comparison: null,
        },
        base64: encodeBase64Json({
          ...JSON.parse(window.atob(baseFilter.base64)),
          date: { $gte: range.start.short, $lt: range.endExclusive },
        }),
        timestamp: Date.now(),
      };
      const combined = withDaypart(nextFilter, filter?.data?.daypart);
      setFilter(combined);
      setFilterValue(combined);
    } catch (error) {
      setSeasonError(error.message);
    }
  };

  useEffect(() => {
    if (!filter || !user?.data?.usrKey) return undefined;
    let active = true;
    const { data } = filter;
    const period = { start: data.date.start, end: data.date.end };
    const previousPeriod = data.season
      ? data.comparison?.date || null
      : getPreviousPeriod(period);
    setReport({
      loading: true,
      outlets: [],
      previousOutlets: null,
      period,
      error: null,
    });
    const request = ({ start, end }) =>
      getMenuReport({
        apiBaseUrl: chimeraserv.urlGet,
        usrKey: user.data.usrKey,
        reportType: "menu-engineering",
        chaKeys: data.chains.map((chain) => chain.chaKey),
        startDate: new Date(start * 1000),
        endDate: new Date(end * 1000),
        revCent: (data.revCent || []).map((center) => center.name),
        daypart: data.daypart,
      });
    const names = data.chains || [];
    const brands = data.brands || [];
    const normalize = (outlets) =>
      (Array.isArray(outlets) ? outlets : []).map((outlet) => ({
        ...outlet,
        name: getOutletName(outlet, { chains: names, brands }),
        items: Array.isArray(outlet.items) ? outlet.items : [],
      }));

    Promise.all([
      request(period),
      // Period comparison is optional; the dashboard still loads if it fails.
      currentPage === "menu-dashboard" && previousPeriod
        ? request(previousPeriod).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([outlets, previousOutlets]) => {
        if (!active) return;
        setReport({
          loading: false,
          outlets: normalize(outlets),
          previousOutlets: previousOutlets ? normalize(previousOutlets) : null,
          period,
          error: null,
        });
      })
      .catch((error) => {
        if (active) {
          setReport({
            loading: false,
            outlets: [],
            previousOutlets: null,
            period,
            error: error.message,
          });
        }
      });

    return () => {
      active = false;
    };
  }, [chimeraserv.urlGet, currentPage, filter, user?.data?.usrKey]);

  const viewProps = useMemo(
    () => ({
      filter,
      outlets: report.outlets,
      previousOutlets: report.previousOutlets,
      period: report.period,
      error: report.error,
    }),
    [
      filter,
      report.error,
      report.outlets,
      report.period,
      report.previousOutlets,
    ]
  );

  return (
    <section
      className={`menuEngineeringPage${
        currentPage === "menu-order" ? " menuOrderPage" : ""
      }`}
    >
      <Helmet>
        <title>{`${page.title} | Menu Engineering | Chimera Solutions | Insights`}</title>
      </Helmet>

      <header className="menuEngineeringHeading">
        <div>
          <div className="menuEngineeringEyebrow">MENU PERFORMANCE</div>
          <h1>{page.title}</h1>
          <p>{page.description}</p>
        </div>
        {filter?.data?.date && (
          <div className="menuEngineeringDate">
            {filter.data.season ? (
              `${filter.data.season.businessDates.start} - ${filter.data.season.businessDates.end}`
            ) : (
              <>
                {new Date(filter.data.date.start * 1000).toLocaleDateString()} –{" "}
                {new Date(filter.data.date.end * 1000).toLocaleDateString()}
              </>
            )}
          </div>
        )}
      </header>

      <div
        className={`menuEngineeringFilters${
          currentPage === "menu-dashboard" ? " menuEngineeringFiltersLarge" : ""
        }${currentPage === "menu-order" ? " menuEngineeringFiltersNoDate" : ""}`}
      >
        <Filter
          key={location.pathname}
          location={location.pathname}
          hideMisc={currentPage === "menu-order"}
          setFilter={handleSetFilter}
        />
      </div>

      {baseFilter && currentPage !== "menu-order" && (
        <>
          <SeasonTabs
            key={seasonBrandId}
            brandId={seasonBrandId}
            value={filter?.data?.season?.id || ""}
            onChange={selectSeason}
          />
          {seasonError && <p role="alert">{seasonError}</p>}
          <DaypartTabs
            key={`dayparts-${seasonBrandId}`}
            brandId={seasonBrandId}
            value={filter?.data?.daypart?.id || ""}
            onChange={selectDaypart}
          />
          {daypartError && <p role="alert">{daypartError}</p>}
        </>
      )}

      <main className="menuEngineeringContent">
        {!filter ? (
          <div className="menuEngineeringEmpty">
            <span className="menuEngineeringEmptyIcon" aria-hidden="true">
              ◉
            </span>
            <h2>Choose your menu filters</h2>
            <p>
              Choose a period and the group, brand, outlet, or revenue centers
              you want to explore, then generate the report.
            </p>
          </div>
        ) : report.loading ? (
          <div className="menuEngineeringLoading">
            <Loader />
          </div>
        ) : report.error ? (
          <div className="menuEngineeringError" role="alert">
            <strong>We couldn’t load menu performance.</strong>
            <span>{report.error}</span>
          </div>
        ) : (
          <Routes>
            <Route
              path="menu-dashboard"
              element={<MenuDashboard {...viewProps} />}
            />
            <Route
              path="multi-outlet-comparison"
              element={<MultiOutletComparison {...viewProps} />}
            />
            <Route path="menu-order" element={<MenuOrder {...viewProps} />} />
            <Route
              path="/"
              element={<Navigate to="menu-dashboard" replace />}
            />
            <Route
              path="*"
              element={
                <Navigate to="/app/menuEngineering/menu-dashboard" replace />
              }
            />
          </Routes>
        )}
      </main>
    </section>
  );
}

export default MenuEngineering;
